import { createFileRoute, Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-v2.jpg.asset.json";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { button3DClass } from "@/components/button-3d";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "Digital Marketing Company in India | MG Content Labs" },
      {
        name: "description",
        content:
          "MG Content Labs is a Delhi-based digital marketing company pairing editorial craft with semantic SEO, answer engine optimization, and measurable growth.",
      },
      { property: "og:title", content: "About MG Content Labs — Digital Marketing Company in India" },
      {
        property: "og:description",
        content:
          "A Delhi-based digital marketing agency built for the AI era: editorial craft, semantic SEO, AEO, and measurement that compounds.",
      },
      { property: "og:url", content: "https://mgcontentlabs.com/about" },
      { property: "og:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
      { name: "twitter:image", content: `https://mgcontentlabs.com${heroAsset.url}` },
    ],
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/about" }],
  }),
  component: AboutPage,
});

const values = [
  {
    title: "Editorial First",
    description:
      "We believe the best marketing is indistinguishable from genuinely useful publishing. Every asset must earn the reader's attention before it earns a click.",
  },
  {
    title: "Scientific Growth",
    description:
      "We don't guess. We measure. Rankings, citations, read-through, and pipeline attribution guide every editorial decision we make.",
  },
  {
    title: "AI-Native Thinking",
    description:
      "Search is no longer just Google. We architect content for the AI era: LLM citations, entity coverage, and semantic authority across every discovery surface.",
  },
  {
    title: "Long-Term Partnerships",
    description:
      "Content compounds. We work as an embedded extension of your team, not a one-off vendor, so authority keeps stacking quarter after quarter.",
  },
];

const process = [
  {
    step: "01",
    title: "Discover",
    description:
      "We audit your current content, competitive landscape, technical foundation, and ideal customer profiles to find the highest-leverage opportunities.",
  },
  {
    step: "02",
    title: "Architect",
    description:
      "We design a topical map, editorial calendar, and distribution plan that aligns every piece of content with business outcomes.",
  },
  {
    step: "03",
    title: "Produce",
    description:
      "Our editors and strategists ship long-form articles, semantic SEO pages, executive social posts, and flagship assets on a predictable cadence.",
  },
  {
    step: "04",
    title: "Measure",
    description:
      "We report on rankings, AI citations, engagement, and pipeline impact — then iterate the strategy based on what the data reveals.",
  },
];

function AboutPage() {
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      {/* Hero */}
      <header className="bg-brand-muted py-24 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">About Us</span>
          <h1 className="mt-6 font-serif text-5xl leading-[1.05] md:text-7xl">
            A digital marketing company <span className="italic text-brand-accent">built for AI search.</span>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-brand-deep/70">
            MG Content Labs is a premium digital marketing agency built for the AI era. We combine
            editorial craft, semantic SEO, and scientific measurement to turn content into a
            compounding growth engine for B2B and SaaS brands.
          </p>
        </div>
      </header>

      {/* Our Story */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Our Story</span>
              <h2 className="mt-4 font-serif text-4xl">From keyword chasing to authority building.</h2>
              <div className="mt-6 space-y-6 text-brand-deep/70">
                <p>
                  MG Content Labs was founded on a simple observation: most digital marketing is built
                  for a search engine that no longer exists. The old playbook — chase keywords,
                  publish thin posts, pray for rankings — is breaking as AI engines and AI Overviews
                  answer questions directly, often before the user ever clicks.
                </p>
                <p>
                  We set out to build a different kind of agency. One where editorial quality comes
                  first, where every asset is designed for semantic authority, and where growth is
                  measured in rankings, citations, engagement, and pipeline — not vanity traffic.
                </p>
                <p>
                  Today, we help growth-stage B2B and SaaS companies become the definitive answer in
                  their category: ranked by Google, cited by ChatGPT and Perplexity, and shared by
                  the communities where their buyers actually spend attention.
                </p>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              {[
                { value: "6.7M+", label: "Words optimized" },
                { value: "50+", label: "Brands served" },
                { value: "43%", label: "Avg. engagement lift" },
                { value: "<45%", label: "Bounce rate benchmark" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="border border-brand-deep/10 bg-brand-cream p-8 text-center"
                >
                  <div className="font-sans text-4xl font-bold tabular-nums tracking-tight text-brand-accent">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-deep/60">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-brand-deep py-24 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Our Values</span>
            <h2 className="mt-4 font-serif text-4xl">How we work</h2>
            <p className="mx-auto mt-4 max-w-2xl text-white/60">
              Four principles guide every engagement, every editorial decision, and every report we share.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {values.map((v) => (
              <div key={v.title} className="border border-white/10 bg-white/5 p-8">
                <h3 className="font-serif text-2xl">{v.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/70">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Our Method</span>
            <h2 className="mt-4 font-serif text-4xl">The Authority Engine</h2>
            <p className="mx-auto mt-4 max-w-2xl text-brand-deep/60">
              A repeatable system that turns content into compounding authority.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {process.map((p) => (
              <div key={p.step} className="border-t border-brand-accent pt-8">
                <div className="font-sans text-3xl font-bold text-brand-accent">{p.step}</div>
                <h3 className="mt-4 font-serif text-xl">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-deep/70">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="bg-brand-muted py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-brand-accent">Why MG Content Labs</span>
              <h2 className="mt-4 font-serif text-4xl">Not a vendor. An embedded growth partner.</h2>
              <p className="mt-6 text-brand-deep/70">
                We combine the rigor of a strategy consultancy with the craft of an editorial studio
                and the technical depth of an SEO agency. The result is a content engine that keeps
                compounding long after the first pieces are published.
              </p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              {[
                "Founder-led editorial oversight",
                "Semantic SEO + AEO from day one",
                "AI citation and LLM-readiness",
                "Pipeline-attributed reporting",
                "Embedded Slack collaboration",
                "Flexible monthly retainers",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <span className="mt-1 text-brand-accent">✓</span>
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="font-serif text-4xl md:text-5xl">
            Ready to become the <span className="italic text-brand-accent">definitive answer?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-brand-deep/70">
            Book a 30-minute strategy call. We'll audit your current content engine and map three
            growth plays you can ship this quarter.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className={button3DClass("primary")}>
              Book a Strategy Call
            </Link>
            <Link to="/pricing" className={button3DClass("secondary")}>
              Compare Plans
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
